import type {
  GridProps
} from '@chakra-ui/react'
import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  FormControl,
  Grid,
  Icon,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Select,
  Skeleton,
  Spinner,
  Stack,
  Switch,
  Text
} from '@chakra-ui/react'
import { faFilter } from '@fortawesome/pro-light-svg-icons'
import { faFilter as fasFilter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Offer } from 'BrokerTsLib/src/dtos'
import type { Context } from 'react'
import { useContext, useMemo } from 'react'

import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePrintContext } from '@/context/PrintContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { usePriceFormat } from '@/hooks/usePriceFormat'

import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { FormSectionHeader } from '../../containers/forms/FormSectionHeader'
import type { ICalculationState } from '../../containers/steps/vehicle/Step2/Step2'
import { Step2SidebarIds } from '../../containers/steps/vehicle/Step2/Step2'
import type { IOffersContext } from '../../context/OffersProvider'
import { SortBy } from '../../context/OffersProvider'
import { useSidebar } from '../../context/SidebarContext'
import { FormLabel } from '../FormLabel'
import type { FilterOptions } from '../QuotesFilter'
import { getQuotesFilterDS } from '../QuotesFilter'
import { getOffersContext } from '../WizzardHeader/WizzardHeader'

export interface InsuranceQuoteGroupHeaderProps extends GridProps {
  visibleCount?: number
  totalCount?: number
  enableAddVariant?: boolean
  enableShowAll?: boolean
  enableSortByInsurer?: boolean
  enableSortByPrice?: boolean
  calculationState?: ICalculationState
  sectionTitle?: string
  onShowAllQuotes?: () => void
  onAddNewVariant?: () => void
}

export const InsuranceQuoteGroupHeader = ({
  visibleCount,
  totalCount,
  enableAddVariant = true,
  enableSortByPrice = true,
  enableSortByInsurer = true,
  enableShowAll = true,
  sectionTitle,
  onShowAllQuotes,
  onAddNewVariant,
  calculationState,
  ...rest
}: InsuranceQuoteGroupHeaderProps) => {
  const { pageSections } = useSidebar()
  const { app } = useApp()
  const { priceFormat } = usePriceFormat()

  const ctx = getOffersContext(app!) as unknown as Context<IOffersContext<Offer>>
  const context = useContext(ctx)
  const {
    sortBy,
    setSortBy: changeSortBy,
    showAll,
    offers,
    setShowWithErrors,
    showWithErrors,
    showFilter,
    filter,
    resetFilter,
    setShowFilter,
    filterAplied,
    priceRange,
    priceRangeFilter,
    handleChangeFilterPriceRange,
    handleChangeFilterInsurer
  } = context

  const { isDisabled, insuranceParams } = useInsuranceParams()

  const anyErrorQuote = offers.filter(x => x.errors.filter(y => !y.softError).length > 0 || x.price == 0).length > 0
  const { printMode } = usePrintContext()
  const { translate } = useTranslationsContext()

  const dataSource = useMemo(() => {
    const ofrs = !showAll ? offers.filter(x => x.included) : offers
    return getQuotesFilterDS(ofrs, insuranceParams?.baseParams?.variants)
  }, [offers, showAll, insuranceParams?.baseParams?.variants])

  if (!context || !calculationState?.isCalculating && !calculationState?.isCalculated)
    return null

  return (
    <Card {...rest}>
      <FormSectionHeader
        number={calculationState?.isCalculating ? <Spinner size="xs" /> : pageSections.find(x => x.id == Step2SidebarIds.Quotes)?.index}
        asHeading={true}
      >
        <Flex
          sx={{
            w: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {sectionTitle}

          {(enableSortByPrice || enableSortByInsurer) &&
            <Skeleton isLoaded={calculationState?.isCalculated}>
              <Box>
                <Select
                  variant="filled"
                  size="sm"
                  value={sortBy}
                  onChange={(e) => changeSortBy!(e.target.value as SortBy)}
                >
                  {enableSortByPrice && <option value={SortBy.Price}>{translate('sortByPrice', 'Dle pojistného')}</option>}
                  {enableSortByInsurer && <option value={SortBy.Insurer}>{translate('sortByInsurer', 'Dle pojišťovny')}</option>}
                </Select>
              </Box>
            </Skeleton>}

          {!!visibleCount && !!totalCount &&
            <Skeleton isLoaded={calculationState?.isCalculated}>
              <Badge sx={{ fontSize: 'xs', fontWeight: 'bold' }}>
                {translate('showed', 'Zobrazeno')} {visibleCount}/{totalCount}
              </Badge>
            </Skeleton>
          }

          <Grid
            sx={{
              w: {
                base: '100%',
                md: 'auto',
              },
              gap: 2,
              alignItems: 'center',
              justifyContent: 'center',
              justifyItems: 'center',
              gridAutoFlow: {
                base: 'row',
                md: 'column',
              }
            }}
          >
            <Stack w={160}>
              {isIn(app!, Apps.property, Apps.travel)
                ? (
                  <>
                    <Skeleton isLoaded={calculationState?.isCalculated}>
                      <Button variant="ghost" onClick={() => setShowFilter(!showFilter)} py={1.5}>
                        <Icon as={FontAwesomeIcon} icon={filterAplied ? fasFilter : faFilter} size="xl" />
                        {showFilter ? 'Skrýt filtr' : 'Filtrovat'}
                      </Button>
                    </Skeleton>
                  </>
                )
                : (
                  <>
                    {enableShowAll &&
                      <Skeleton isLoaded={calculationState?.isCalculated}>
                        <Button variant="ghost" onClick={() => onShowAllQuotes?.()} py={0}>
                          {showAll ? translate('step2.filter.hideNotSelected', 'Skrýt nevybrané') : translate('step2.filter.showNotSelected', 'Zobrazit nevybrané')}
                        </Button>
                      </Skeleton>
                    }
                  </>
                )
              }
            </Stack>

            {enableAddVariant &&
              <Skeleton isLoaded={calculationState?.isCalculated}>
                <Button colorScheme="primary"
                  onClick={() => onAddNewVariant?.()}
                  isDisabled={isDisabled}>
                  {translate('step2.addNewVariant', 'Přidat další variantu pojištění')}
                </Button>
              </Skeleton>
            }
          </Grid>
        </Flex>
      </FormSectionHeader>


      {showFilter &&
        <Grid
          p={8}
          templateColumns='repeat(4, 1fr)'
          columnGap={8}
          rowGap={4}
        >

          {dataSource.insurers &&
            <Stack>
              <FormLabel>{translate('insurers', 'Pojišťovny')}</FormLabel>

              <Stack>
                {dataSource.insurers.map(insurer => {
                  return (
                    <Checkbox
                      key={insurer.insurerId}
                      size='sm'
                      value={insurer.insurerId.toString()}
                      isChecked={filter.displayOnlyInsurers.some(x => x == insurer.insurerId)}
                      onChange={(e) => handleChangeFilterInsurer(insurer.insurerId, e.target.checked)}
                    >
                      {insurer.insurerName}
                    </Checkbox>
                  )
                })}
              </Stack>
            </Stack>
          }

          {priceRange[0] !== priceRange[1] &&
            <Stack>
              <FormLabel>{translate('price', 'Cena')}</FormLabel>

              <Box sx={{ pl: 3, pr: 3, mb: 6, mt: 6 }}>

                <RangeSlider
                  aria-label={['min', 'max']}
                  step={100}
                  min={priceRange.at(0)}
                  max={priceRange.at(1)}
                  value={priceRangeFilter}
                  onChange={(range) => handleChangeFilterPriceRange(range[0], range[1])}
                  title={`min. ${priceFormat(priceRange[0], 'N/A')}, max. ${priceFormat(priceRange[1], 'N/A')}`}
                >
                  <RangeSliderTrack>
                    <RangeSliderFilledTrack />
                  </RangeSliderTrack>
                  <RangeSliderThumb index={0}>
                    <Text fontSize={'sm'} fontWeight={'bold'} sx={{ position: 'absolute', left: -1, bottom: 6 }}>{priceFormat(priceRangeFilter[0], 'N/A')}</Text>
                  </RangeSliderThumb>
                  <RangeSliderThumb index={1}>
                    <Text fontSize={'sm'} fontWeight={'bold'} sx={{ position: 'absolute', right: -1, top: 6 }}>{priceFormat(priceRangeFilter[1], 'N/A')}</Text>
                  </RangeSliderThumb>
                </RangeSlider>
              </Box>
            </Stack>
          }

          <Stack>
            <FormLabel>{translate('other', 'Ostatní')}</FormLabel>

            {enableShowAll &&
              <FormControl display='flex' alignItems='center'>
                <FormLabel htmlFor='showExcluded' cursor='pointer' gap={3} alignItems={'center'} fontSize={'sm'}>
                  <Switch id="showExcluded"
                    size='sm'
                    isChecked={showAll}
                    onChange={() => onShowAllQuotes?.()}
                  />

                  {translate('step2.filter.showNotSelected', 'Zobrazit nevybrané')}
                </FormLabel>

              </FormControl>
            }

            {anyErrorQuote &&
              !printMode && // v print modu se chybove nezobrazuji
              <FormControl display='flex' alignItems='center'>
                <FormLabel htmlFor='showWithErrors' cursor='pointer' gap={3} alignItems={'center'} fontSize={'sm'}>
                  <Switch id="showWithErrors"
                    size='sm'
                    isChecked={showWithErrors}
                    onChange={() => setShowWithErrors(!showWithErrors)}
                  />
                  {translate('step2.filter.showWithErrors', 'Zobrazit chybné kalkulace')}
                </FormLabel>

              </FormControl>
            }
          </Stack>

          {filterAplied &&
            <Flex gridColumn='1 / -1' justifyContent='center'>
              <Button
                variant='link'
                colorScheme='gray'
                onClick={resetFilter}
                _focus={{ boxShadow: 'none' }}
              >
                {translate('step2.filter.resetFilter', 'Resetovat filtr')}
              </Button>
            </Flex>
          }
        </Grid>
      }
    </Card>
  )
}